import { AutoComplete, message } from 'antd';
import { Trash2 } from 'lucide-react';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type SpaceUser, useSpaceUsers } from '@/api/spaces';
import { Avatar, Tag } from '@/components/ui';
import { getConsistentAvatarColor, getUserDisplayName } from '@/features/sharing-profile/utils/avatarUtils';
import { useCurrentUserAvatar } from '@/features/user/hooks/useProfileSettings';
import type { GroupData } from '@/features/user-management/interfaces/interfaces';
import type { User } from '@/interfaces/api';

interface AddUsersToGroupFromProps {
  groupData: GroupData;
  setGroupData: Dispatch<SetStateAction<GroupData>>;
}

export const AddUsersToGroupForm = ({ groupData, setGroupData }: AddUsersToGroupFromProps) => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const { data: usersData = [], refetch: usersRefetch } = useSpaceUsers(spaceId) as { data: SpaceUser[]; refetch: () => void };

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Array<{ value: string; label: ReactNode }>>([]);
  const [activeTag, setActiveTag] = useState<number | null>(0);

  const existingEmails = new Set(groupData.users.map((user) => user.email));

  const addEmail = (email: string) => {
    if (existingEmails.has(email)) {
      message.warning('User already added');
      return;
    }

    const selectedUser = usersData.find((user: SpaceUser) => user.email === email) as User | undefined;
    if (selectedUser) {
      setGroupData({
        ...groupData,
        users: [...groupData.users, selectedUser],
      });
      setInputValue('');
    }
  };

  const removeEmail = (email: string) => {
    setGroupData({
      ...groupData,
      users: groupData.users.filter((user: User) => user.email !== email),
    });
  };

  const filterOptions = (value: string) => {
    const lower = value.toLowerCase();
    return usersData
      .filter((user: SpaceUser) => !existingEmails.has(user.email) && user.email.toLowerCase().includes(lower) && !user.is_invite)
      .map((user: SpaceUser) => ({
        value: user.email,
        label: (
          <div className="flex items-center gap-2">
            <Avatar className="mr-3" color={getConsistentAvatarColor(user)} name={getUserDisplayName(user)} size={36} />
            <div className="flex flex-col text-start">
              <span className="font-medium text-sm text-[#262322]">
                {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'Unknown Name'}
              </span>
              <span className="font-normal text-[11px] text-[#6B6B6B]">{user.email}</span>
            </div>
          </div>
        ),
      }));
  };

  const handleSearch = (value: string) => {
    setInputValue(value);
    setOptions(filterOptions(value));
  };

  const handleFocus = () => {
    if (!inputValue) {
      setOptions(filterOptions(''));
    }
  };

  useEffect(() => {
    usersRefetch();
  });

  const { username, color } = useCurrentUserAvatar();

  return (
    <>
      <div className="relative w-full flex items-center gap-x-2.5 pt-5">
        <AutoComplete
          className="focus:!ring-0 focus:!border-0"
          options={options}
          placeholder="Search users"
          style={{ width: 300 }}
          value={inputValue}
          onChange={setInputValue}
          onFocus={handleFocus}
          onSearch={handleSearch}
          onSelect={addEmail}
        />
      </div>
      <label className="w-full px-3 py-1 font-normal text-xs text-[#808080]">
        Only users who have been previously created can be added.
      </label>

      <div className="mt-[58px]">
        {groupData.users.length > 0 &&
          groupData.users.map((user: User) => (
            <div className="flex items-center gap-28 w-4/6 h-[40px] mb-4" key={user.email}>
              <div className="flex items-center gap-2 w-[272px]">
                <Avatar className="mr-3" color={getConsistentAvatarColor(user)} name={getUserDisplayName(user)} size={36} />
                <div className="flex flex-col text-start">
                  <span className="font-medium text-sm text-[#262322]">
                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'Unknown Name'}
                  </span>
                  <span className="font-normal text-[11px] text-[#6B6B6B]">{user.email}</span>
                </div>
              </div>
              <div className="flex w-full items-center justify-between">
                <div className="flex gap-[11px] justify-start h-6">
                  {user.roles?.map((role: string, index: number) => (
                    <Tag isActive={activeTag === index} key={index} tagType={role} onHover={() => setActiveTag(index)} />
                  ))}
                </div>
                <Trash2 className="w-6 h-6 text-[#ABB5BE] hover:text-gold-darker cursor-pointer" onClick={() => removeEmail(user.email)} />
              </div>
            </div>
          ))}
      </div>
    </>
  );
};
